/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useState } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Column } from "react-table";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import Cookies from "js-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";

import "./AdminView.scss";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import useToken from "hooks/useToken";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import { getCompanies, getUsers } from "api";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import { getRequest } from "api/axiosClient";
import { Typeahead } from "react-bootstrap-typeahead";
import { useStateMachine } from "little-state-machine";
import { resetState } from "store/store";
import { clearFiltersFromLocalStorage } from "helpers/localStorage";
import { Company } from "api/models";

type AdminViewProps = {
  setIsLoggedInAs: Function;
  getPermissions: Function;
  setIsLoading: Function;
};

function AdminView(props: AdminViewProps) {
  const { setIsLoggedInAs, getPermissions, setIsLoading } = props;
  const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;

  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { clearToken } = useToken();
  const { clearPermissions } = usePermissions();
  const [userType, setUserType] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const { actions } = useStateMachine({ resetState });

  const handleCompanyChange = (selected: Company[]) => {
    if (!selected.length) return;
    setSelectedCompany(selected[0]);
    instance.setGlobalFilter({ company: selected[0].id });
  };

  const sortAlphabetically = (companiesData: any) => [...companiesData].sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const params = userType ? { type: userType.charAt(0).toUpperCase() + userType.slice(1) } : {};

    getCompanies({ params })
      .then((res) => setCompanies(sortAlphabetically(res.data.results)))
      .catch((err) => errorToast(err));
  }, [userType]);

  const columns = useMemo<Column[]>(
    () => [
      {
        Header: "Name",
        accessor: "full_name",
        sortable: true,
        Cell: ({ value }) => value,
      },
      {
        Header: "Company name",
        accessor: "company_name",
        sortable: false,
        Cell: ({ value }) => value,
      },
      {
        Header: "Email",
        accessor: "email",
        sortable: true,
        Cell: ({ value }) => value,
      },
      {
        Header: "Payment locations",
        accessor: "payment_locations",
        sortable: true,
        style: {
          maxWidth: 200,
          textOverflow: "ellipsis",
          overflow: "hidden",
        },
        Cell: ({ value }) => (
          <OverlayTrigger
            placement="top-start"
            delay={{ show: 50, hide: 50 }}
            overlay={<Tooltip id="button-tooltip">{value}</Tooltip>}
          >
            <span>{value || "-"}</span>
          </OverlayTrigger>
        ),
      },
      {
        Header: "Last login",
        accessor: "last_login",
        Cell: ({ value }) => {
          if (!value) return "-";
          if (moment().year() === moment(value).year()) return moment(value).format("MMM D - HH:mm");
          return moment(value).format("MMM D YYYY - HH:mm");
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => <span className={value === "Active" ? "text-success" : "text-warning"}>{value}</span>,
      },
      {
        Header: "Role",
        accessor: "role",
        sortable: true,
        Cell: ({ value }) => value,
      },
      {
        Header: "",
        accessor: "id",
        Cell: ({ value }) => (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button
              variant="primary"
              label="Login as"
              onClick={() => handleLoginAs(value)}
              data-testid={testIds.STAFF_LOGIN_AS_BUTTON}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const { instance, fetching, totalData } = useTableRequest(getUsers, columns, {
    filters: (globalFilter) => ({
      ...(userType === "provider" && { providers: true }),
      ...(userType === "shipper" && { shippers: true }),
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.company && { company: globalFilter.company }),
    }),
    deps: [userType],
  });

  const handleLoginAs = (userId: number) => {
    // Reset store data
    actions.resetState();
    // Remove any preexisting permissions
    localStorage.removeItem("cargobase_userpermissions");
    setIsLoading(true);
    getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/staff/login-as/${userId}`)
      .then((res: any) => {
        Cookies.set("cargobase_logged_in_as_token", res.data.access);
        getPermissions();
        navigate("/");
      })
      .then(() => setIsLoggedInAs(true))
      .catch((err) => errorToast(err));
  };

  const handleLogout = () => {
    clearToken();
    clearPermissions();
    clearFiltersFromLocalStorage();
    Cookies.remove("cargobase_logged_in_as_token");
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <div className="admin-container">
      <Card body className="shadow-none quote-container">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex flex-wrap align-items-center">
            <h3 className="mb-0 mr-3">Admin Panel</h3>
            <GlobalTableSearch
              currentFilters={instance.state.globalFilter}
              setFilters={instance.setGlobalFilter}
              filterKey="query"
              eventContext="Admin View"
            />
          </div>
          <div>
            <Button
              variant="link"
              className="font-16"
              label={<FontAwesomeIcon icon={faSignOutAlt} />}
              onClick={handleLogout}
            />
          </div>
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          withoutBody
          headerLeft={
            <PillTabs
              id="admin-status-tabs"
              activeKey={userType}
              onSelect={(eventKey: any) => setUserType(eventKey || "")}
              tabItems={[
                { eventKey: "", title: "All" },
                { eventKey: "shipper", title: "Shippers" },
                { eventKey: "provider", title: "Providers" },
              ]}
            />
          }
          headerRight={
            <div className="company-filter">
              <Typeahead
                options={companies}
                onChange={handleCompanyChange}
                minLength={4}
                placeholder="Search for a company..."
                selected={selectedCompany ? [selectedCompany] : []}
                labelKey={"name" as any}
              />
            </div>
          }
        />
      </Card>
    </div>
  );
}

export default AdminView;
